import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Input, Space, Tag, Tooltip } from 'antd';
import { IconBuilding, IconFileText } from '@tabler/icons-react';
import { CrudMode, CrudSchemaFields, renderListToTag, renderSwitchToTag, renderMultiListToTags, type EnsoFormItem } from '@ohpen/enso-ui';
import { ExportOutlined, QuestionCircleOutlined } from '@ant-design/icons';

import { CasePermission, FieldType, FirmType } from '@pmp/api';
import { TabProps } from '../interfaces';
import { CrudFormCard } from './CrudFormCard';
import { getHighlightProps, resetFieldsOnTypeChange } from '../utils';
import styles from '../styles.module.css';

export const RegistrationDetails: React.FC<TabProps> = ({
  form,
  mode,
  selectsOptions,
  entityId,
  populateFcaData,
  fcaModifiedFields,
  removeFcaModifiedField,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const readonly = mode === CrudMode.VIEW;
  const frn = Form.useWatch(['registrationDetails', 'frn'], form);
  const type = Form.useWatch(['registrationDetails', 'type'], form);

  const isFcaRegistered = useMemo(() => type !== 'NOT_FCA_REGISTERED', [type]);

  const populateData = useCallback(() => {
    setLoading(true);
    if (populateFcaData) {
      populateFcaData(frn).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [frn, populateFcaData]);

  const firmColumns: EnsoFormItem[] = useMemo(
    () => [
      {
        title: t('lender.firms.labels.currentStatus'),
        dataIndex: ['registrationDetails', 'isActive'],
        valueType: 'switch',
        initialValue: mode === CrudMode.CREATE ? false : undefined,
        readonly,
        fieldProps: readonly
          ? undefined
          : {
              checkedChildren: t('lender.firms.labels.active'),
              unCheckedChildren: t('lender.firms.labels.inactive'),
            },
        render: renderSwitchToTag,
        colProps: { span: 24 },
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.type'),
            dataIndex: ['registrationDetails', 'type'],
            valueType: 'select',
            fieldProps: {
              options: selectsOptions[FieldType.FIRM_TYPE],
              tagColor: 'blue',
              className: getHighlightProps(['registrationDetails', 'type'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('registrationDetails', 'type'),
              onChange: (value: string) => resetFieldsOnTypeChange(form, value),
            },
            formItemProps: {
              label: (
                <span>
                  {t('lender.firms.labels.type')}
                  {isFcaRegistered && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {t('lender.firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
              rules: [{ required: true }],
            },
            readonly,
            render: renderListToTag,
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.principalFirm'),
            dataIndex: ['registrationDetails', 'principalIds'],
            valueType: 'select',
            fieldProps: {
              options: (selectsOptions[FieldType.PRINCIPALS] ?? []).filter(o => o.value !== entityId),
              tagColor: 'blue',
              mode: 'multiple',
            },
            formItemProps: {
              rules: [{ required: true }],
              hidden: type !== FirmType.APPOINTED_REPRESENTATIVE,
            },
            readonly,
            render: renderMultiListToTags,
            colProps: { span: 12 },
          },
        ],
      },
      // {
      //   title: t('lender.firms.labels.currentStatus'),
      //   dataIndex: ['registrationDetails', 'currentStatus'],
      //   valueType: 'checkbox',
      //   fieldProps: {
      //     options: [{ label: t('lender.firms.labels.firmActive'), value: 'firmActive' }],
      //   },
      //   colProps: { span: 24 },
      // },
      {
        valueType: 'group',
        colProps: { span: 24 },
        hideInForm: !isFcaRegistered,
        columns: [
          {
            title: t('lender.firms.labels.frn'),
            dataIndex: ['registrationDetails', 'frn'],
            valueType: 'text',
            readonly,
            renderText: val => String(val),
            renderFormItem: () => (
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name={['registrationDetails', 'frn']} noStyle>
                  <Input style={{ width: '100%' }} inputMode="numeric" disabled={mode !== CrudMode.CREATE} />
                </Form.Item>
                <Button type="primary" onClick={populateData} disabled={readonly} loading={loading}>
                  {t('lender.firms.buttons.populateData')}
                </Button>
              </Space.Compact>
            ),
            formItemProps: {
              tooltip: readonly ? null : t('lender.firms.labels.frnPopulateTooltip'),
              rules: [{ required: isFcaRegistered }],
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.fcaRegister'),
            dataIndex: ['registrationDetails', 'fcaRegisterLink'],
            key: 'fcaRegisterLink',
            valueType: 'text',
            colProps: { span: 12 },

            renderFormItem: () => {
              const frn = form?.getFieldValue(['registrationDetails', 'frn']);
              if (!frn) return '-';

              const url = `https://register.fca.org.uk/s/search?q=${encodeURIComponent(frn)}&type=Companies`;

              return (
                <a href={url} target="_blank" rel="noreferrer">
                  {t('lender.firms.labels.goToWebsite')}
                  <ExportOutlined />
                </a>
              );
            },
          },
        ],
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.name'),
            dataIndex: ['registrationDetails', 'name'],
            valueType: 'text',
            readonly,
            formItemProps: {
              label: (
                <span>
                  {t('lender.firms.labels.name')}
                  {isFcaRegistered && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {t('lender.firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
              rules: [{ required: true }],
            },
            fieldProps: {
              className: getHighlightProps(['registrationDetails', 'name'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('registrationDetails', 'name'),
            },
            colProps: { span: 12 },
          },
        ],
      },
      ...(mode === CrudMode.CREATE ? [{
        valueType: 'group' as const,
        columns: [
          {
            title: t('lender.firms.labels.reference'),
            dataIndex: ['registrationDetails', 'reference'],
            valueType: 'text' as const,
            readonly,
            colProps: { span: 12 },
            formItemProps: {
              tooltip: readonly ? null : t('lender.firms.labels.referenceTooltip'),
            },
          },
        ],
      }] : [{
        valueType: 'group' as const,
        columns: [
          {
            title: t('lender.firms.labels.ohpenId'),
            dataIndex: ['registrationDetails', 'ohpenId'],
            valueType: 'text' as const,
            readonly: mode === CrudMode.VIEW,
            fieldProps: {
              disabled: true,
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.reference'),
            dataIndex: ['registrationDetails', 'reference'],
            valueType: 'text' as const,
            readonly: mode === CrudMode.VIEW,
            colProps: { span: 12 },
            formItemProps: {
              tooltip: mode === CrudMode.VIEW ? null : t('lender.firms.labels.referenceTooltip'),
            },
          },
        ],
      }]),
    ],
    [t, populateData, readonly, mode, type, form, loading, selectsOptions, fcaModifiedFields, removeFcaModifiedField, isFcaRegistered, entityId],
  );

  const registrationColumns: EnsoFormItem[] = useMemo(
    () => [
      {
        valueType: 'group',
        columns: [
          {
            dataIndex: ['registrationDetails', 'fcaStatus'],
            valueType: 'text',
            readonly,
            fieldProps: {
              className: getHighlightProps(['registrationDetails', 'fcaStatus'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('registrationDetails', 'fcaStatus'),
            },
            formItemProps: {
              label: (
                <span>
                  {t('lender.firms.labels.fcaRegisterStatus')}
                  {isFcaRegistered && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {t('lender.firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.nacfbRegistration'),
            dataIndex: ['registrationDetails', 'nacfbNumber'],
            valueType: 'text',
            readonly,
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.operations'),
            dataIndex: ['registrationDetails', 'operations'],
            valueType: 'checkbox',
            fieldProps: {
              options: [
                { label: t('lender.firms.labels.givesAdvice'), value: 'ADVICE' },
                { label: t('lender.firms.labels.packaging'), value: 'PACKAGE' },
                { label: t('lender.firms.labels.actAsPrincipal'), value: 'NETWORK', disabled: type !== FirmType.DIRECTLY_AUTHORISED },
                { label: t('lender.firms.labels.mortgageClub'), value: 'MORTGAGE_CLUB', disabled: type === FirmType.NOT_FCA_REGISTERED },
              ],
              disabled: readonly,
              style: {
                display: 'flex',
                flexDirection: 'column',
                rowGap: 4,
              },
            },
            formItemProps: {
              label: t('lender.firms.labels.operations'),
              rules: [{ required: true, message: t('lender.firms.labels.operationsRequired') }],
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group',
        colProps: { span: 24 },
        title: (
          <Space size={4}>
            {t('lender.firms.labels.activitiesPermissions')}
            <Tooltip title={t('lender.firms.labels.activitiesPermissionsTooltip')}>
              <QuestionCircleOutlined className={styles.tooltipIcon} />
            </Tooltip>
          </Space>
        ),
        columns: [
          {
            dataIndex: ['registrationDetails', 'btl'],
            valueType: 'select',
            fieldProps: { options: selectsOptions[FieldType.CASE_PERMISSION_TYPE] },
            formItemProps: { label: t('lender.firms.labels.btl'), tooltip: t('lender.firms.labels.btlTooltip') },
            initialValue: CasePermission.CREATE_AND_MANAGE,
            readonly,
            render: renderListToTag,
            colProps: { span: 8 },
          },
          {
            dataIndex: ['registrationDetails', 'rbtl'],
            valueType: 'select',
            fieldProps: { options: selectsOptions[FieldType.CASE_PERMISSION_TYPE], disabled: !isFcaRegistered },
            formItemProps: { label: t('lender.firms.labels.rbtl'), tooltip: t('lender.firms.labels.rbtlTooltip') },
            initialValue: CasePermission.CREATE_AND_MANAGE,
            readonly,
            render: renderListToTag,
            colProps: { span: 8 },
          },
          {
            dataIndex: ['registrationDetails', 'rmc'],
            valueType: 'select',
            fieldProps: { options: selectsOptions[FieldType.CASE_PERMISSION_TYPE], disabled: !isFcaRegistered },
            formItemProps: { label: t('lender.firms.labels.rmc') },
            initialValue: CasePermission.CREATE_AND_MANAGE,
            readonly,
            render: renderListToTag,
            colProps: { span: 8 },
          },
        ],
      },
    ],
    [readonly, selectsOptions, t, type, fcaModifiedFields, removeFcaModifiedField, isFcaRegistered],
  );

  return (
    <>
      <CrudFormCard icon={<IconBuilding />} title={t('lender.firms.labels.firmTitle')}>
        <CrudSchemaFields schema={firmColumns} form={form} readonly={readonly} component={false} />
      </CrudFormCard>
      <CrudFormCard icon={<IconFileText />} title={t('lender.firms.labels.registrationDetails')}>
        <CrudSchemaFields schema={registrationColumns} form={form} readonly={readonly} component={false} />
      </CrudFormCard>
    </>
  );
};
